/**
 * Auth portal (StaticSmashAuth) integration helpers.
 *
 * Real login is delegated to the external Smash auth portal: when an
 * unauthenticated user reaches a private (dashboard) route, they are redirected
 * to `<AUTH_DOMAIN>/signin?sourceUrl=<this app origin>`. After a successful
 * login the portal redirects back to `sourceUrl` with an `authCode` query
 * parameter, which the app exchanges for an identity via `verifyAuthorizationCode`.
 */

/**
 * Resolve the auth portal domain from the environment.
 *
 * @returns The portal base URL (e.g. `https://auth.fromsmash.com`), read from
 *          `REACT_APP_AUTH_DOMAIN`.
 */
export const getAuthPortalDomain = (): string => {
    return process.env.REACT_APP_AUTH_DOMAIN as string;
};

/**
 * Redirect the browser to the auth portal sign-in page so the user can
 * authenticate. The portal returns to `sourceUrl` with an `authCode` once login
 * succeeds.
 *
 * @param sourceUrl URL the portal should redirect back to after login. Defaults
 *        to the current app origin.
 */
export const redirectToAuthPortal = (sourceUrl: string = window.location.origin): void => {
    const url = `${getAuthPortalDomain()}/signin?sourceUrl=${encodeURIComponent(sourceUrl)}`;
    window.location.href = url;
};

/**
 * Determine whether a Smash JWT is session-bound (issued by the portal via an
 * authorization code) rather than backed by a refresh token. Session-bound
 * tokens are renewed through the session (`renewToken()` with no argument on the
 * 10-2025 IAM SDK); legacy tokens are renewed with their refresh token.
 *
 * @param token Encoded JWT (the `token.token` of an identity).
 * @returns `true` when the token carries a `sessionId`/`session` claim; `false`
 *          when it does not or cannot be decoded.
 */
export const tokenHasSession = (token: string): boolean => {
    try {
        const payload = JSON.parse(atob(token.split('.')[1])) as { sessionId?: string; session?: unknown };
        return !!payload.sessionId || !!payload.session;
    } catch {
        return false;
    }
};
